/* eslint-disable @typescript-eslint/no-non-null-assertion */
import Tile from 'features/tiles/tile';
import TileGridContainer from 'features/tiles/tileGridContainer';
import { FC, useMemo } from 'react';
import { RootState } from 'app/store';
import { useDispatch, useSelector } from 'react-redux';
import ChartBridge from 'features/charts/strategies/chart-bridge';
import Loading from 'common/components/loading/loading';
import Slider from 'common/components/inputs/slider';
import { createBudgetOptimiserCharts } from './budget-optimiser-charts';
import {
  AxisChartByNumberPrediction,
  AxisChartByString
} from 'features/charts/chart.models';
import { PRIOR_SPEND } from './budget-optimiser-chart';
import { CustomTable } from 'common/components/table/customTable';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { SpendAmount } from './budget-optimiser.types';
import {
  createBudgetOptimiserTableColumns,
  createBudgetOptimiserTableRows
} from './budget-optimiser-table';
import {
  getSpendAmounts,
  onSpendAmountChanged
} from '../header-controls/header-controls.slice';
import { useGetDataScienceMmmSegmentDataQuery } from 'app/apis/queryApi';
import MmmHeaderControls from '../header-controls/mmm-header-controls';

const MmmBudgetOptimiserPage: FC<Record<string, unknown>> = () => {
  const dispatch = useDispatch();

  const { siteId } = useSelector((state: RootState) => state.selectedSite);
  const { selectedSegmentKey } = useSelector(
    (state: RootState) => state.mmmHeaderControls
  );
  const spendAmounts = useSelector(getSpendAmounts);

  const { data: mmmModelOutput, isFetching: isFetchingModelOutput } =
    useGetDataScienceMmmSegmentDataQuery(
      {
        siteId,
        segment: selectedSegmentKey
      },
      {
        skip: selectedSegmentKey.length < 1
      }
    );

  const charts = useMemo(() => {
    if (!isFetchingModelOutput && mmmModelOutput) {
      return createBudgetOptimiserCharts(mmmModelOutput);
    }
  }, [mmmModelOutput, isFetchingModelOutput]);

  const columnDefs = useMemo(() => {
    return createBudgetOptimiserTableColumns(
      mmmModelOutput !== undefined ? mmmModelOutput.forecastUnit : 'GBP'
    );
  }, [mmmModelOutput]);

  const budgetOptimiserTableData = useMemo(() => {
    if (mmmModelOutput && spendAmounts.length > 0) {
      return createBudgetOptimiserTableRows(mmmModelOutput, spendAmounts);
    }

    return [];
  }, [mmmModelOutput, spendAmounts]);

  const getDiminishingReturnsChart = (): AxisChartByNumberPrediction => {
    const dataPointSets = spendAmounts.flatMap((spendAmount) =>
      charts!.diminishingReturnsChartOptimisationDataPointSets.filter(
        (dps) =>
          dps.dataPointSetName === spendAmount.sourceCategoryName &&
          dps.xValue ===
            (mmmModelOutput !== undefined
              ? spendAmount.optimisedSpendAmount * mmmModelOutput.dayMultiplier
              : 0)
      )
    );

    return {
      ...charts!.diminishingReturnsChart,
      dataPointSetWithOptimisations: dataPointSets
    };
  };

  const getBudgetOptimiserChart = (): AxisChartByString => {
    const newDataPointSets = spendAmounts.flatMap((spendAmount) => {
      const spendamount =
        spendAmount.optimisedSpendAmount === spendAmount.maximumSpend
          ? spendAmount.optimisedSpendAmount - 1
          : spendAmount.optimisedSpendAmount;

      const dataPointSet =
        charts!.budgetOptimiserChartOptimisationDataPointSets.get(
          spendAmount.sourceCategoryName
        )![spendamount];

      return dataPointSet;
    });

    const priorSpends = charts!.budgetOptimiserChart.dataPointSets.filter(
      (dps) => dps.dataPointSetName === PRIOR_SPEND
    );

    return {
      ...charts!.budgetOptimiserChart,
      dataPointSets: [...priorSpends, ...newDataPointSets]
    };
  };

  const mmmTable = useReactTable({
    data: budgetOptimiserTableData,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel()
  });

  const onSliderChanged = (
    spendAmount: SpendAmount,
    newSpendAmountValue: number
  ): void => {
    const newSpendAmount: SpendAmount = {
      ...spendAmount,
      optimisedSpendAmount: newSpendAmountValue
    };

    dispatch(onSpendAmountChanged(newSpendAmount));
  };

  return (
    <div>
      <MmmHeaderControls />

      <div className="grid grid-cols-12 gap-3">
        <TileGridContainer className="col-span-12 grid grid-cols-12 gap-3 items-stretch">
          <div className="col-span-12 md:col-span-6 flex flex-col">
            <h4 className="mb-1">Diminishing Returns</h4>
            <Tile className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm grow flex flex-row items-center">
              <div className="px-3 h-92 w-full">
                {charts && !isFetchingModelOutput ? (
                  <ChartBridge chart={getDiminishingReturnsChart()} />
                ) : (
                  <Loading />
                )}
              </div>
            </Tile>
          </div>

          <div className="col-span-12 md:col-span-6 flex flex-col">
            <h4 className="mb-1">Budget Optimiser Table</h4>
            <Tile className="bg-white border rounded border-neutral-200 p-2.5 shadow-sm grow">
              <div className="p-0">
                {charts && !isFetchingModelOutput ? (
                  <CustomTable
                    key={`mmm-budget-table`}
                    componentId={'mmm-budget-table-component-id'}
                    table={mmmTable}
                    fontsizeClass="text-sm"
                    cellClasses="p-3 truncate"
                  />
                ) : (
                  <Loading />
                )}
              </div>
            </Tile>
          </div>

          <div className="col-span-12 md:col-span-6">
            <h4 className="mb-1">Budget Optimiser</h4>
            <Tile>
              <div className="px-3 h-92">
                {charts && !isFetchingModelOutput ? (
                  <ChartBridge chart={getBudgetOptimiserChart()} />
                ) : (
                  <Loading />
                )}
              </div>
            </Tile>
          </div>

          <div className="col-span-12 md:col-span-6 flex items-stretch flex-col">
            <h4 className="mb-1">Optimised Spend Controls</h4>
            <Tile className="grow">
              <div className="px-3">
                {charts && !isFetchingModelOutput ? (
                  <div>
                    {spendAmounts.map((spendAmount, index) => (
                      <div key={`on-risk-change-${index}`} className="mb-3">
                        <label className="block text-sm">
                          {spendAmount.sourceCategoryName}
                        </label>
                        <Slider
                          className="w-full"
                          value={spendAmount.optimisedSpendAmount}
                          max={spendAmount.maximumSpend}
                          onChange={(newSpendAmountValue) =>
                            onSliderChanged(spendAmount, newSpendAmountValue)
                          }
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <Loading />
                )}
              </div>
            </Tile>
          </div>
        </TileGridContainer>
      </div>
    </div>
  );
};

export default MmmBudgetOptimiserPage;
